// Draft.js keeps every line of a code block as a block of its own, and draftToMarkdown fences each block
// separately - so three lines of pasted code were saved as three code boxes. Consecutive code lines are joined
// into one block before saving; one block holding newlines is written as a single fence, and reads back as one.

interface Range {
  offset: number;
  length: number;
}

interface RawBlock {
  type: string;
  text: string;
  depth?: number;
  inlineStyleRanges?: Range[];
  entityRanges?: Range[];
}

export const CODE_BLOCK = 'code-block';

const shift = <R extends Range>(ranges: R[] | undefined, by: number): R[] =>
  (ranges ?? []).map(r => ({ ...r, offset: r.offset + by }));

const keepWithin = <R extends Range>(ranges: R[] | undefined, start: number, end: number): R[] =>
  (ranges ?? [])
    .map(r => {
      const from = Math.max(r.offset, start);
      const to = Math.min(r.offset + r.length, end);
      return { ...r, offset: from - start, length: to - from };
    })
    .filter(r => r.length > 0);

/**
 * An empty line at the start or end of a code block means nothing, but Enter after the last line of code leaves
 * one - it showed as a blank line inside the code box. Empty lines between lines of code are kept.
 */
const trimEmptyEdgeLines = <T extends RawBlock>(block: T): T => {
  const start = /^\n*/.exec(block.text)[0].length;
  const end = start + block.text.slice(start).replace(/\n+$/, '').length;
  if (start === 0 && end === block.text.length) return block;
  return {
    ...block,
    text: block.text.slice(start, end),
    inlineStyleRanges: keepWithin(block.inlineStyleRanges, start, end),
    entityRanges: keepWithin(block.entityRanges, start, end)
  };
};

/**
 * Joins each run of consecutive code blocks into one, keeping any styles and entities on their own text, and
 * trims empty lines from the start and end of the result.
 */
export const mergeCodeLines = <T extends RawBlock>(blocks: T[]): T[] =>
  joinCodeLines(blocks).map(block => (block.type === CODE_BLOCK ? trimEmptyEdgeLines(block) : block));

const joinCodeLines = <T extends RawBlock>(blocks: T[]): T[] =>
  blocks.reduce<T[]>((merged, block) => {
    const previous = merged[merged.length - 1];
    if (block.type !== CODE_BLOCK || previous?.type !== CODE_BLOCK) {
      merged.push(block);
      return merged;
    }
    const by = previous.text.length + 1;
    merged[merged.length - 1] = {
      ...previous,
      text: `${previous.text}\n${block.text}`,
      inlineStyleRanges: [...(previous.inlineStyleRanges ?? []), ...shift(block.inlineStyleRanges, by)],
      entityRanges: [...(previous.entityRanges ?? []), ...shift(block.entityRanges, by)]
    };
    return merged;
  }, []);
